import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CloudArrowUp, FloppyDisk, DownloadSimple, ArrowCounterClockwise, Trash, Database, ShieldCheck, Clock, HardDrives, LockKey, BellRinging, Archive, Recycle } from "@phosphor-icons/react";

const MODULES = [{ v: "itsm", l: "ITSM" }, { v: "soc", l: "SOC" }];
const CATS = [{ v: "config", l: "Config" }, { v: "data", l: "Data" }, { v: "logs", l: "Logs" }];

function Picker({ modules, categories, setModules, setCategories, prefix }) {
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
  return (
    <div className="flex flex-wrap gap-x-8 gap-y-3">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Modules</div>
        <div className="flex gap-4">
          {MODULES.map(m => (
            <label key={m.v} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox data-testid={`${prefix}-mod-${m.v}`} checked={modules.includes(m.v)} onCheckedChange={() => toggle(modules, setModules, m.v)} /> {m.l}
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Categories</div>
        <div className="flex gap-4">
          {CATS.map(c => (
            <label key={c.v} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox data-testid={`${prefix}-cat-${c.v}`} checked={categories.includes(c.v)} onCheckedChange={() => toggle(categories, setCategories, c.v)} /> {c.l}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function BackupRestore() {
  const [cfg, setCfg] = useState(null);
  const [backups, setBackups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [sas, setSas] = useState("");
  const [recips, setRecips] = useState("");
  // manual selection
  const [runMods, setRunMods] = useState(["itsm", "soc"]);
  const [runCats, setRunCats] = useState(["config", "data", "logs"]);
  // restore dialog
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [resMods, setResMods] = useState([]);
  const [resCats, setResCats] = useState([]);
  const [restoring, setRestoring] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // ticket lifecycle
  const [life, setLife] = useState(null);
  const [savingLife, setSavingLife] = useState(false);
  const [cannedList, setCannedList] = useState([]);
  const [archives, setArchives] = useState([]);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);

  const runReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-tickets", { confirm: resetConfirm });
      const d = data.deleted || {};
      toast.success(`Tickets reset — ${d.incidents || 0} tickets, ${d.changes || 0} changes, ${d.problems || 0} problems removed. Numbering reset.`);
      setResetOpen(false); setResetConfirm("");
      loadArchives();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setResetting(false); }
  };

  const loadCfg = () => api.get("/backup/config").then(r => { setCfg(r.data); setRecips((r.data.alert_recipients || []).join(", ")); }).catch(() => {});
  const loadList = () => api.get("/backup/list").then(r => setBackups(r.data)).catch(() => {});
  const loadLife = () => api.get("/incidents/lifecycle/config").then(r => setLife(r.data)).catch(() => {});
  const loadArchives = () => api.get("/incidents/archives").then(r => setArchives(r.data)).catch(() => {});
  const loadCanned = () => api.get("/canned-responses").then(r => setCannedList(r.data)).catch(() => {});
  useEffect(() => { loadCfg(); loadList(); loadLife(); loadArchives(); loadCanned(); }, []);

  const setL = (k, v) => setLife(c => ({ ...c, [k]: v }));
  const saveLife = async () => {
    setSavingLife(true);
    try {
      const { data } = await api.put("/incidents/lifecycle/config", {
        auto_close_enabled: life.auto_close_enabled, auto_close_days: life.auto_close_days,
        archive_enabled: life.archive_enabled, archive_months: life.archive_months,
        reopen_window_enabled: life.reopen_window_enabled, reopen_window_days: life.reopen_window_days,
        reopen_reject_canned_id: life.reopen_reject_canned_id,
      });
      setLife(data);
      toast.success("Lifecycle settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingLife(false); }
  };
  const restoreArchive = async (a) => {
    try { await api.post(`/incidents/archives/${a.id}/restore`); toast.success(`${a.number} restored to queue`); loadArchives(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  const setDS = (k, v) => setCfg(c => ({ ...c, default_selection: { ...c.default_selection, [k]: v } }));

  const saveCfg = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: cfg.enabled, interval_hours: cfg.interval_hours, retention: cfg.retention,
        azure_enabled: cfg.azure_enabled, azure_account: cfg.azure_account, azure_container: cfg.azure_container,
        encryption_enabled: cfg.encryption_enabled, default_selection: cfg.default_selection,
        alert_email_enabled: cfg.alert_email_enabled, alert_teams_enabled: cfg.alert_teams_enabled,
        alert_recipients: recips, notify_success: cfg.notify_success,
        notify_failure: cfg.notify_failure, notify_azure_fail: cfg.notify_azure_fail,
      };
      if (sas.trim()) payload.azure_sas_token = sas.trim();
      const { data } = await api.put("/backup/config", payload);
      setCfg(data); setSas(""); setRecips((data.alert_recipients || []).join(", "));
      toast.success("Backup settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const runBackup = async () => {
    if (!runMods.length || !runCats.length) { toast.error("Pick at least one module and category"); return; }
    setRunning(true);
    try {
      const { data } = await api.post("/backup/run", { modules: runMods, categories: runCats });
      toast.success(`Backup created · ${data.total_records} records${data.azure_uploaded ? " · Azure ✓" : ""}`);
      loadList(); loadCfg();
    } catch (e) { toast.error(e?.response?.data?.detail || "Backup failed"); }
    finally { setRunning(false); }
  };

  const download = async (b) => {
    try {
      const token = localStorage.getItem("helix_token");
      const res = await fetch(`${API}/backup/${b.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = b.name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const del = async (b) => {
    try { await api.delete(`/backup/${b.id}`); toast.success("Backup deleted"); loadList(); }
    catch { toast.error("Delete failed"); }
  };

  const openRestore = (b) => { setRestoreTarget(b); setResMods(b.modules || []); setResCats(b.categories || []); setPreview(null); };

  useEffect(() => {
    if (!restoreTarget) return;
    if (!resMods.length || !resCats.length) { setPreview(null); return; }
    let active = true;
    setPreviewing(true);
    api.post(`/backup/${restoreTarget.id}/restore`, { modules: resMods, categories: resCats, dry_run: true })
      .then(r => { if (active) setPreview(r.data); })
      .catch(() => { if (active) setPreview(null); })
      .finally(() => { if (active) setPreviewing(false); });
    return () => { active = false; };
  }, [restoreTarget, resMods, resCats]);

  const doRestore = async () => {
    setRestoring(true);
    try {
      const { data } = await api.post(`/backup/${restoreTarget.id}/restore`, { modules: resMods, categories: resCats });
      toast.success(`Restored · ${data.total_inserted} missing record(s) re-inserted`);
      setRestoreTarget(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    finally { setRestoring(false); }
  };

  const fmtSize = (n) => n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n > 1024 ? `${(n / 1024).toFixed(1)} KB` : `${n} B`;
  if (!cfg) return <div className="p-6 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="backup-restore-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Data Protection</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black">Backup &amp; Restore</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Create per-collection JSON archives (.zip) for ITSM &amp; SOC — split by Config, Data and Logs. Archives are stored here and, when configured, auto-uploaded to Azure Blob Storage. Restore is non-destructive (only missing records are inserted).</p>
      </div>

      {/* Manual backup */}
      <Card className="p-6 rounded-sm border-border">
        <div className="flex items-center gap-2 mb-4"><Database size={18} className="text-primary" /><h2 className="font-display text-lg font-bold">Create a Backup</h2></div>
        <Picker modules={runMods} categories={runCats} setModules={setRunMods} setCategories={setRunCats} prefix="run" />
        <Button data-testid="run-backup-btn" onClick={runBackup} disabled={running} className="mt-5 rounded-sm font-mono text-xs uppercase tracking-widest">
          <CloudArrowUp size={15} className="mr-2" />{running ? "Backing up…" : "Run Backup Now"}
        </Button>
      </Card>

      {/* Azure + schedule */}
      <Card className="p-6 rounded-sm border-border space-y-6">
        <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /><h2 className="font-display text-lg font-bold">Azure Blob Storage &amp; Schedule</h2></div>

        <div className="space-y-4">
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm flex items-center gap-2"><CloudArrowUp size={15} className="text-primary" /> Auto-upload backups to Azure Blob Storage</span>
            <Switch data-testid="azure-enabled-switch" checked={cfg.azure_enabled} onCheckedChange={(v) => set("azure_enabled", v)} />
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase">Storage account name</Label><Input data-testid="azure-account-input" className="mt-1 rounded-sm" placeholder="mystorageacct" value={cfg.azure_account || ""} onChange={(e) => set("azure_account", e.target.value)} /></div>
            <div><Label className="text-xs uppercase">Container</Label><Input data-testid="azure-container-input" className="mt-1 rounded-sm" placeholder="backups" value={cfg.azure_container || ""} onChange={(e) => set("azure_container", e.target.value)} /></div>
          </div>
          <div>
            <Label className="text-xs uppercase">SAS token (fallback auth) {cfg.azure_has_sas && <span className="text-emerald-500 normal-case">· saved</span>}</Label>
            <Input data-testid="azure-sas-input" type="password" className="mt-1 rounded-sm font-mono" placeholder={cfg.azure_has_sas ? "•••••• (leave blank to keep)" : "?sv=...  (optional)"} value={sas} onChange={(e) => setSas(e.target.value)} />
            <p className="text-[11px] text-muted-foreground mt-1">Most secure: leave SAS blank and grant the app's Managed Identity the <span className="font-mono">Storage Blob Data Contributor</span> role (Entra ID auth is used automatically). Use a container-scoped SAS only if managed identity isn't available.</p>
          </div>
          <label className="flex items-center justify-between gap-3 cursor-pointer border-t border-border pt-4">
            <span className="text-sm flex items-center gap-2"><LockKey size={15} className="text-primary" /> Encrypt archives at rest (AES / Fernet) {cfg.encryption_ready && <span className="text-[10px] font-mono text-emerald-500 uppercase">key active</span>}</span>
            <Switch data-testid="encryption-switch" checked={cfg.encryption_enabled} onCheckedChange={(v) => set("encryption_enabled", v)} />
          </label>
          <p className="text-[11px] text-muted-foreground -mt-2">When on, each archive is encrypted before it's stored/uploaded, so backups stay confidential at rest in Azure. The app decrypts automatically on download &amp; restore.</p>
        </div>

        <div className="border-t border-border pt-5 space-y-4">
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm flex items-center gap-2"><Clock size={15} className="text-primary" /> Scheduled automatic backups</span>
            <Switch data-testid="schedule-enabled-switch" checked={cfg.enabled} onCheckedChange={(v) => set("enabled", v)} />
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase">Interval (hours)</Label><Input data-testid="interval-input" type="number" min="1" className="mt-1 rounded-sm w-32" value={cfg.interval_hours} onChange={(e) => set("interval_hours", Number(e.target.value))} /></div>
            <div><Label className="text-xs uppercase">Keep last N backups</Label><Input data-testid="retention-input" type="number" min="1" className="mt-1 rounded-sm w-32" value={cfg.retention} onChange={(e) => set("retention", Number(e.target.value))} /></div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground mb-2">Scheduled backup contents</div>
            <Picker modules={cfg.default_selection.modules} categories={cfg.default_selection.categories} setModules={(v) => setDS("modules", v)} setCategories={(v) => setDS("categories", v)} prefix="sched" />
          </div>
          {cfg.last_run && <p className="text-[11px] font-mono text-muted-foreground">Last run: {new Date(cfg.last_run).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST · {cfg.last_status}</p>}
        </div>

        <div className="border-t border-border pt-5 space-y-4">
          <div className="flex items-center gap-2"><BellRinging size={16} className="text-primary" /><h3 className="font-display font-bold">Backup Alerts</h3></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="flex items-center justify-between gap-3 cursor-pointer rounded-sm border border-border px-3 py-2">
              <span className="text-sm">Email alerts</span>
              <Switch data-testid="alert-email-switch" checked={cfg.alert_email_enabled} onCheckedChange={(v) => set("alert_email_enabled", v)} />
            </label>
            <label className="flex items-center justify-between gap-3 cursor-pointer rounded-sm border border-border px-3 py-2">
              <span className="text-sm">Teams alerts</span>
              <Switch data-testid="alert-teams-switch" checked={cfg.alert_teams_enabled} onCheckedChange={(v) => set("alert_teams_enabled", v)} />
            </label>
          </div>
          {cfg.alert_email_enabled && (
            <div>
              <Label className="text-xs uppercase">Alert recipients (comma-separated)</Label>
              <Input data-testid="alert-recipients-input" className="mt-1 rounded-sm" placeholder="admin@company.com, ops@company.com" value={recips} onChange={(e) => setRecips(e.target.value)} />
            </div>
          )}
          {cfg.alert_teams_enabled && <p className="text-[11px] text-muted-foreground">Teams alerts post to the webhook configured under <span className="font-mono">Admin → Teams Integration</span>.</p>}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Notify me on</div>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox data-testid="notify-success" checked={cfg.notify_success} onCheckedChange={(v) => set("notify_success", !!v)} /> Success</label>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox data-testid="notify-failure" checked={cfg.notify_failure} onCheckedChange={(v) => set("notify_failure", !!v)} /> Failure</label>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox data-testid="notify-azure-fail" checked={cfg.notify_azure_fail} onCheckedChange={(v) => set("notify_azure_fail", !!v)} /> Azure upload failure</label>
            </div>
          </div>
        </div>

        <Button data-testid="save-backup-cfg-btn" onClick={saveCfg} disabled={saving} className="rounded-sm font-mono text-xs uppercase tracking-widest">
          <FloppyDisk size={15} className="mr-2" />{saving ? "Saving…" : "Save Settings"}
        </Button>
      </Card>

      {/* Ticket lifecycle */}
      {life && (
      <Card className="p-6 rounded-sm border-border space-y-5" data-testid="lifecycle-card">
        <div className="flex items-center gap-2"><Recycle size={18} className="text-primary" /><h2 className="font-display text-lg font-bold">Incident Ticket Lifecycle</h2></div>
        <p className="text-sm text-muted-foreground -mt-2 max-w-2xl">Automate the active queue: close resolved tickets that have gone quiet, then archive long-closed tickets out of the queue. Archived tickets are stored safely and can be restored anytime.</p>

        <div className="border-t border-border pt-4 space-y-3">
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm flex items-center gap-2"><Clock size={15} className="text-primary" /> Auto-close resolved tickets</span>
            <Switch data-testid="autoclose-switch" checked={!!life.auto_close_enabled} onCheckedChange={(v) => setL("auto_close_enabled", v)} />
          </label>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Close a ticket that stays in <span className="font-mono text-foreground">resolved</span> for</span>
            <Input data-testid="autoclose-days-input" type="number" min="1" className="rounded-sm w-20 h-9" value={life.auto_close_days} onChange={(e) => setL("auto_close_days", Number(e.target.value))} disabled={!life.auto_close_enabled} />
            <span>day(s)</span>
          </div>
          {life.last_auto_close && <p className="text-[11px] font-mono text-muted-foreground">Last run: {new Date(life.last_auto_close).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST · {life.last_auto_close_count} closed</p>}
        </div>

        <div className="border-t border-border pt-4 space-y-3">
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm flex items-center gap-2"><Archive size={15} className="text-primary" /> Archive long-closed tickets</span>
            <Switch data-testid="archive-switch" checked={!!life.archive_enabled} onCheckedChange={(v) => setL("archive_enabled", v)} />
          </label>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Move a <span className="font-mono text-foreground">closed</span> ticket out of the queue after</span>
            <Input data-testid="archive-months-input" type="number" min="1" className="rounded-sm w-20 h-9" value={life.archive_months} onChange={(e) => setL("archive_months", Number(e.target.value))} disabled={!life.archive_enabled} />
            <span>month(s)</span>
          </div>
          {life.last_archive && <p className="text-[11px] font-mono text-muted-foreground">Last run: {new Date(life.last_archive).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST · {life.last_archive_count} archived</p>}
        </div>

        <div className="border-t border-border pt-4 space-y-3" data-testid="reopen-window-section">
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm flex items-center gap-2"><ArrowCounterClockwise size={15} className="text-primary" /> Reopen window for email replies</span>
            <Switch data-testid="reopen-window-switch" checked={!!life.reopen_window_enabled} onCheckedChange={(v) => setL("reopen_window_enabled", v)} />
          </label>
          <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
            <span>Don't reopen a <span className="font-mono text-foreground">closed</span> ticket if a reply arrives more than</span>
            <Input data-testid="reopen-window-days-input" type="number" min="1" max="90" className="rounded-sm w-20 h-9" value={life.reopen_window_days ?? 2} onChange={(e) => setL("reopen_window_days", Number(e.target.value))} disabled={!life.reopen_window_enabled} />
            <span>working day(s) after close (weekends excluded)</span>
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Auto-reply canned response (sent asking the requester to open a new ticket)</span>
            <select
              data-testid="reopen-canned-select"
              className="w-full h-9 rounded-sm border border-border bg-background text-sm px-2 disabled:opacity-50"
              value={life.reopen_reject_canned_id || ""}
              disabled={!life.reopen_window_enabled}
              onChange={(e) => setL("reopen_reject_canned_id", e.target.value || null)}
            >
              <option value="">Built-in default message</option>
              {cannedList.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
            <p className="text-[11px] text-muted-foreground">Placeholders <code className="font-mono">{"{{ticket_number}}"}</code> and <code className="font-mono">{"{{customer}}"}</code> are filled in automatically. Applies to human email replies only — automated alert recurrences are unaffected.</p>
          </div>
        </div>

        <Button data-testid="save-lifecycle-btn" onClick={saveLife} disabled={savingLife} className="rounded-sm font-mono text-xs uppercase tracking-widest">
          <FloppyDisk size={15} className="mr-2" />{savingLife ? "Saving…" : "Save Lifecycle Settings"}
        </Button>

        {archives.length > 0 && (
          <div className="border-t border-border pt-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Archived tickets ({archives.length})</div>
            <div className="overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full text-sm" data-testid="archives-table">
                <thead className="text-[11px] font-mono uppercase text-muted-foreground">
                  <tr><th className="text-left px-3 py-2">Ticket</th><th className="text-left px-3 py-2">Title</th><th className="text-left px-3 py-2">Client</th><th className="text-left px-3 py-2">Archived</th><th></th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {archives.map(a => (
                    <tr key={a.id} data-testid={`archive-row-${a.number}`} className="hover:bg-accent/30">
                      <td className="px-3 py-2 font-mono text-xs">{a.number}</td>
                      <td className="px-3 py-2 max-w-xs truncate">{a.title}</td>
                      <td className="px-3 py-2 text-xs">{a.customer_name || "—"}</td>
                      <td className="px-3 py-2 text-xs">{a.archived_at ? new Date(a.archived_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <button data-testid={`archive-restore-${a.number}`} onClick={() => restoreArchive(a)} title="Restore to queue" className="text-amber-600 dark:text-amber-400 hover:opacity-70"><ArrowCounterClockwise size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
      )}

      {/* Backups list */}
      <Card className="p-0 rounded-sm border-border overflow-hidden">
        <div className="flex items-center gap-2 p-5 border-b border-border"><HardDrives size={18} className="text-primary" /><h2 className="font-display text-lg font-bold">Saved Backups</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="backups-table">
            <thead className="bg-muted/40 text-[11px] font-mono uppercase text-muted-foreground">
              <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Created</th><th className="text-left px-4 py-3">Scope</th><th className="text-left px-4 py-3">Records</th><th className="text-left px-4 py-3">Size</th><th className="text-left px-4 py-3">Source</th><th className="text-left px-4 py-3">Azure</th><th></th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {backups.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No backups yet.</td></tr>}
              {backups.map(b => (
                <tr key={b.id} data-testid={`backup-row-${b.id}`} className="hover:bg-accent/30">
                  <td className="px-4 py-3 font-mono text-xs">{b.name}</td>
                  <td className="px-4 py-3 text-xs">{new Date(b.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                  <td className="px-4 py-3 text-[11px] font-mono uppercase">{(b.modules || []).join("+")} · {(b.categories || []).join("/")}</td>
                  <td className="px-4 py-3">{b.total_records}</td>
                  <td className="px-4 py-3 text-xs">{fmtSize(b.size_bytes)}</td>
                  <td className="px-4 py-3 text-[11px] font-mono uppercase">{b.source}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${b.azure_uploaded ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10" : "border-border text-muted-foreground"}`}>{b.azure_uploaded ? "Uploaded" : "Local"}</span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button data-testid={`backup-download-${b.id}`} onClick={() => download(b)} title="Download" className="text-primary hover:opacity-70 mr-3"><DownloadSimple size={16} /></button>
                    <button data-testid={`backup-restore-${b.id}`} onClick={() => openRestore(b)} title="Restore" className="text-amber-600 dark:text-amber-400 hover:opacity-70 mr-3"><ArrowCounterClockwise size={16} /></button>
                    <button data-testid={`backup-delete-${b.id}`} onClick={() => del(b)} title="Delete" className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <DialogContent data-testid="restore-dialog">
          <DialogHeader><DialogTitle className="font-display">Restore from backup</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Non-destructive restore — only records that are missing will be re-inserted. Existing records are left untouched.</p>
          <div className="text-xs font-mono text-muted-foreground">{restoreTarget?.name}</div>
          <div className="py-2">
            <Picker modules={resMods} categories={resCats} setModules={setResMods} setCategories={setResCats} prefix="res" />
          </div>
          <div className="rounded-sm border border-border bg-muted/30 p-3 text-sm" data-testid="restore-preview">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Dry-run preview</div>
            {previewing ? <div className="text-muted-foreground text-xs">Calculating…</div>
              : !preview ? <div className="text-muted-foreground text-xs">Select modules &amp; categories to preview.</div>
              : preview.total_inserted === 0 ? <div className="text-xs">Everything is already present — <span className="text-emerald-600 dark:text-emerald-400">nothing would be inserted.</span></div>
              : (
                <div>
                  <div className="mb-1"><span className="font-mono font-bold text-primary" data-testid="restore-preview-total">{preview.total_inserted}</span> record(s) would be re-inserted:</div>
                  <ul className="text-xs font-mono space-y-0.5 max-h-32 overflow-y-auto">
                    {Object.entries(preview.inserted).map(([coll, n]) => (
                      <li key={coll} className="flex justify-between gap-4"><span className="text-muted-foreground">{coll}</span><span>+{n}</span></li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="confirm-restore-btn" onClick={doRestore} disabled={restoring || previewing || !resMods.length || !resCats.length} className="rounded-sm">
              {restoring ? "Restoring…" : preview && preview.total_inserted === 0 ? "Nothing to restore" : `Restore ${preview ? preview.total_inserted : ""} record(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Danger zone: go-live ticket reset */}
      <Card className="p-6 rounded-sm border-red-500/40 mt-6" data-testid="reset-tickets-card">
        <div className="flex items-center gap-2 mb-2"><Trash size={18} className="text-red-600 dark:text-red-400" /><h2 className="font-display text-lg font-bold text-red-600 dark:text-red-400">Danger Zone — Reset ticket data</h2></div>
        <p className="text-sm text-muted-foreground max-w-2xl mb-4">For go-live: permanently delete <b className="text-foreground">all Incidents, Service Requests, Security Incidents</b> (plus their comments, attachments, history and archives) and <b className="text-foreground">all Changes and Problems</b>, then reset ticket numbering so the next ticket starts at 01000 again. Users, routing rules, SLA config, assets and KB are left untouched. This cannot be undone.</p>
        <Button data-testid="reset-tickets-btn" onClick={() => { setResetConfirm(""); setResetOpen(true); }} variant="outline"
          className="rounded-sm font-mono text-xs uppercase tracking-widest border-red-500/50 text-red-600 dark:text-red-400 hover:bg-red-500/10">
          <Trash size={14} className="mr-2" />Reset all tickets
        </Button>
      </Card>

      <Dialog open={resetOpen} onOpenChange={(o) => { if (!o) { setResetOpen(false); setResetConfirm(""); } }}>
        <DialogContent className="rounded-sm" data-testid="reset-tickets-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400"><Trash size={18} weight="fill" /> Reset all ticket data — irreversible</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">This permanently deletes <b className="text-foreground">all Incidents / Service Requests / Security Incidents, Changes and Problems</b> and resets numbering to start at 01000. It does not affect users, routing rules, SLA config, assets or KB.</p>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Type RESET to confirm</Label>
              <Input data-testid="reset-confirm-input" value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} placeholder="RESET" className="mt-1 rounded-sm font-mono" autoFocus />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={() => { setResetOpen(false); setResetConfirm(""); }}>Cancel</Button>
            <Button data-testid="reset-confirm-btn" onClick={runReset} disabled={resetting || resetConfirm !== "RESET"}
              className="rounded-sm text-xs font-mono uppercase bg-red-600 hover:bg-red-700 text-white">
              {resetting ? "Resetting…" : "Reset all tickets"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
